(function () {
  "use strict";

  /*
   * =========================================================
   * HAZAAR ATTRIBUTION
   * =========================================================
   *
   * Tracks:
   *
   * - First known marketing touch
   * - Last non-direct marketing touch
   *
   * AWIN — HAZAAR-CONTROLLED LINKS:
   *
   * clickref  = last non-direct channel
   * clickref2 = last campaign
   * clickref3 = first-touch channel
   * clickref4 = Hazaar CTA placement
   * clickref5 = original Hazaar landing page
   * clickref6 = latest creative / utm_content
   *
   * RAKUTEN — HAZAAR-CONTROLLED LINKS:
   *
   * u1 contains:
   *
   * L = last non-direct channel
   * C = campaign
   * F = first-touch channel
   * P = Hazaar CTA placement
   *
   * Example:
   *
   * LgooglecpcCweekdaystudentdiscountFgooglecpcPbrandpage
   *
   * GOCERTIFY:
   *
   * Hazaar passes the last non-direct channel into the
   * GoCertify custom element using:
   *
   * clickref="google_cpc"
   *
   * GoCertify can then retain this alongside its own:
   *
   * student--campaign_id--verification_id--google_cpc
   *
   * Attribution is only stored and passed when Shopify
   * Marketing consent is allowed.
   */


  const FIRST_TOUCH_KEY =
    "hazaar_first_touch";

  const LAST_TOUCH_KEY =
    "hazaar_last_non_direct";


  /*
   * 90-day attribution window.
   */
  const ATTRIBUTION_TTL_MS =
    90 * 24 * 60 * 60 * 1000;


  /*
   * Rakuten Signature u1 supports
   * up to 72 alphanumeric characters.
   */
  const RAKUTEN_U1_MAX_LENGTH =
    72;



  /*
   * =========================================================
   * CLEANING HELPERS
   * =========================================================
   */

  function clean(
    value,
    maxLength = 120
  ) {
    if (!value) {
      return "";
    }

    return String(value)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9._-]/g, "")
      .substring(
        0,
        maxLength
      );
  }


  function cleanPath(value) {

    /*
     * Homepage:
     *
     * "/" becomes "home"
     */
    if (
      !value ||
      value === "/"
    ) {
      return "home";
    }

    return String(value)
      .replace(/^\/+/, "")
      .replace(/\/+/g, "_")
      .substring(
        0,
        120
      );
  }


  function cleanRakuten(
    value,
    maxLength = 120
  ) {

    /*
     * Rakuten u1 should contain
     * alphanumeric characters only.
     *
     * Underscores, hyphens, spaces etc.
     * are removed.
     */

    if (!value) {
      return "";
    }

    return String(value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, "")
      .substring(
        0,
        maxLength
      );
  }



  /*
   * =========================================================
   * SHOPIFY CONSENT
   * =========================================================
   */

  function marketingAllowed() {
    try {

      return Boolean(
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify
          .customerPrivacy
          .marketingAllowed ===
          "function" &&
        window.Shopify
          .customerPrivacy
          .marketingAllowed()
      );

    } catch (error) {

      return false;
    }
  }



  /*
   * =========================================================
   * LOCAL STORAGE
   * =========================================================
   */

  function saveRecord(
    key,
    record
  ) {

    try {

      localStorage.setItem(
        key,
        JSON.stringify({
          ...record,

          expires_at:
            Date.now() +
            ATTRIBUTION_TTL_MS
        })
      );

    } catch (error) {

      console.warn(
        "Hazaar attribution: localStorage unavailable",
        error
      );
    }
  }


  function readRecord(key) {

    try {

      const raw =
        localStorage.getItem(
          key
        );


      if (!raw) {
        return {};
      }


      const record =
        JSON.parse(
          raw
        );


      /*
       * Delete expired attribution.
       */
      if (
        !record ||
        !record.expires_at ||
        Date.now() >
          Number(
            record.expires_at
          )
      ) {

        localStorage.removeItem(
          key
        );

        return {};
      }


      return record;

    } catch (error) {

      return {};
    }
  }



  /*
   * =========================================================
   * AFFILIATE LINK RESTORATION
   * =========================================================
   *
   * Whenever an AWIN or Rakuten link is decorated,
   * we keep its original URL.
   *
   * If Marketing consent is later withdrawn,
   * restore the original affiliate links.
   */

  function restoreAffiliateLinks() {

    const links =
      document.querySelectorAll(
        "a[data-hazaar-original-href]"
      );


    links.forEach(
      function (link) {

        const originalHref =
          link.dataset
            .hazaarOriginalHref;


        if (originalHref) {

          link.href =
            originalHref;
        }


        delete link.dataset
          .hazaarOriginalHref;

        delete link.dataset
          .hazaarPlacement;
      }
    );
  }



  /*
   * =========================================================
   * GOCERTIFY RESTORATION
   * =========================================================
   *
   * GoCertify reads clickref when the custom element is
   * connected to the page. If Marketing consent is later
   * withdrawn, rebuild any Hazaar-managed GoCertify widget
   * without Hazaar attribution so the active component is
   * reinitialised cleanly.
   */

  function copyGoCertifyBaseAttributes(
    sourceWidget,
    targetWidget
  ) {

    Array.from(
      sourceWidget.attributes
    ).forEach(
      function (attribute) {

        /*
         * clickref is handled separately.
         *
         * Hazaar's own bookkeeping attributes
         * should never be copied to the fresh
         * GoCertify component.
         */
        if (
          attribute.name === "clickref" ||
          attribute.name.indexOf(
            "data-hazaar-"
          ) === 0
        ) {
          return;
        }


        targetWidget.setAttribute(
          attribute.name,
          attribute.value
        );
      }
    );
  }



  function restoreGoCertifyWidgets() {

    const widgets =
      Array.from(
        document.querySelectorAll(
          "gocertify-iframe"
        )
      );


    widgets.forEach(
      function (widget) {

        /*
         * Only rebuild widgets that Hazaar
         * previously created with attribution.
         */
        if (
          widget.dataset
            .hazaarGoCertifyBuilt !==
          "true"
        ) {
          return;
        }


        const restoredWidget =
          document.createElement(
            "gocertify-iframe"
          );


        copyGoCertifyBaseAttributes(
          widget,
          restoredWidget
        );


        /*
         * If the original GoCertify component
         * already had a clickref before Hazaar
         * touched it, restore that value.
         */
        if (
          widget.dataset
            .hazaarHadOriginalClickref ===
          "true"
        ) {

          restoredWidget.setAttribute(
            "clickref",
            widget.dataset
              .hazaarOriginalClickref ||
              ""
          );
        }


        /*
         * Inserting a fresh element makes
         * GoCertify initialise again without
         * Hazaar's marketing attribution.
         */
        widget.replaceWith(
          restoredWidget
        );
      }
    );
  }



  /*
   * =========================================================
   * CLEAR ATTRIBUTION
   * =========================================================
   */

  function clearAttribution() {

    try {

      localStorage.removeItem(
        FIRST_TOUCH_KEY
      );

      localStorage.removeItem(
        LAST_TOUCH_KEY
      );

    } catch (error) {

      /*
       * Fail silently if browser storage
       * is unavailable.
       */
    }


    restoreAffiliateLinks();

    restoreGoCertifyWidgets();
  }



  /*
   * =========================================================
   * REFERRER CLASSIFICATION
   * =========================================================
   */

  function classifyReferrer() {

    /*
     * No referrer = Direct.
     */
    if (!document.referrer) {

      return {
        source: "direct",
        medium: "none"
      };
    }


    try {

      const referrerUrl =
        new URL(
          document.referrer
        );


      const host =
        referrerUrl
          .hostname
          .toLowerCase();


      const currentHost =
        window.location
          .hostname
          .toLowerCase();



      /*
       * Ignore Hazaar → Hazaar navigation.
       */
      if (
        host === currentHost ||
        host.endsWith(
          "." + currentHost
        )
      ) {

        return null;
      }



      /*
       * Ignore Shopify admin / preview traffic.
       *
       * This prevents theme testing from
       * becoming a marketing referral.
       */
      if (
        host ===
          "admin.shopify.com" ||
        host.endsWith(
          ".myshopify.com"
        )
      ) {

        return null;
      }



      /*
       * ---------------------------------------------------------
       * Organic Search
       * ---------------------------------------------------------
       */

      if (
        host.includes(
          "google."
        )
      ) {

        return {
          source: "google",
          medium: "organic"
        };
      }


      if (
        host.includes(
          "bing."
        )
      ) {

        return {
          source: "bing",
          medium: "organic"
        };
      }


      if (
        host.includes(
          "yahoo."
        )
      ) {

        return {
          source: "yahoo",
          medium: "organic"
        };
      }


      if (
        host.includes(
          "duckduckgo.com"
        )
      ) {

        return {
          source: "duckduckgo",
          medium: "organic"
        };
      }



      /*
       * ---------------------------------------------------------
       * Organic Social
       * ---------------------------------------------------------
       */

      if (
        host.includes(
          "instagram.com"
        )
      ) {

        return {
          source: "instagram",
          medium: "organic_social"
        };
      }


      if (
        host.includes(
          "tiktok.com"
        )
      ) {

        return {
          source: "tiktok",
          medium: "organic_social"
        };
      }


      if (
        host.includes(
          "facebook.com"
        ) ||
        host.includes(
          "fb.com"
        )
      ) {

        return {
          source: "facebook",
          medium: "organic_social"
        };
      }



      /*
       * Everything else becomes Referral.
       */
      return {
        source:
          host.replace(
            /^www\./,
            ""
          ),

        medium:
          "referral"
      };


    } catch (error) {

      return {
        source: "unknown",
        medium: "referral"
      };
    }
  }



  /*
   * =========================================================
   * CAPTURE ATTRIBUTION
   * =========================================================
   */

  function captureAttribution() {

    /*
     * Do nothing unless Marketing consent
     * has been granted.
     */
    if (!marketingAllowed()) {
      return;
    }


    const params =
      new URLSearchParams(
        window.location.search
      );


    const utmSource =
      params.get(
        "utm_source"
      );

    const utmMedium =
      params.get(
        "utm_medium"
      );

    const utmCampaign =
      params.get(
        "utm_campaign"
      );

    const utmContent =
      params.get(
        "utm_content"
      );

    const gclid =
      params.get(
        "gclid"
      );


    let touch =
      null;



    /*
     * ---------------------------------------------------------
     * 1. Explicit UTM attribution
     * ---------------------------------------------------------
     *
     * UTMs take priority over everything else.
     */

    if (utmSource) {

      touch = {

        source:
          clean(
            utmSource
          ),

        medium:
          clean(
            utmMedium ||
              "unknown"
          ),

        campaign:
          clean(
            utmCampaign
          ),

        content:
          clean(
            utmContent
          ),

        landing_page:
          cleanPath(
            window.location
              .pathname
          ),

        captured_at:
          new Date()
            .toISOString()
      };
    }



    /*
     * ---------------------------------------------------------
     * 2. Google Ads auto-tagging fallback
     * ---------------------------------------------------------
     */

    else if (gclid) {

      touch = {

        source:
          "google",

        medium:
          "cpc",

        campaign:
          "",

        content:
          "",

        landing_page:
          cleanPath(
            window.location
              .pathname
          ),

        captured_at:
          new Date()
            .toISOString()
      };
    }



    /*
     * ---------------------------------------------------------
     * 3. Referrer attribution
     * ---------------------------------------------------------
     */

    else {

      const inferred =
        classifyReferrer();


      /*
       * Internal Hazaar navigation
       * or ignored referrer.
       */
      if (!inferred) {
        return;
      }


      touch = {

        source:
          clean(
            inferred.source
          ),

        medium:
          clean(
            inferred.medium
          ),

        campaign:
          "",

        content:
          "",

        landing_page:
          cleanPath(
            window.location
              .pathname
          ),

        captured_at:
          new Date()
            .toISOString()
      };
    }



    const firstTouch =
      readRecord(
        FIRST_TOUCH_KEY
      );



    /*
     * ---------------------------------------------------------
     * FIRST TOUCH
     * ---------------------------------------------------------
     *
     * Only save the first known marketing source.
     *
     * Direct traffic does not become first-touch attribution.
     */

    if (
      !firstTouch.source &&
      touch.source !==
        "direct"
    ) {

      saveRecord(
        FIRST_TOUCH_KEY,
        touch
      );
    }



    /*
     * ---------------------------------------------------------
     * LAST NON-DIRECT TOUCH
     * ---------------------------------------------------------
     *
     * Every genuine new source can update this.
     *
     * Direct never overwrites it.
     */

    if (
      touch.source !==
        "direct"
    ) {

      saveRecord(
        LAST_TOUCH_KEY,
        touch
      );
    }
  }



  /*
   * =========================================================
   * CHANNEL FORMAT
   * =========================================================
   */

  function channelName(touch) {

    if (
      !touch ||
      !touch.source
    ) {

      return "";
    }


    if (!touch.medium) {

      return clean(
        touch.source
      );
    }


    return clean(
      touch.source +
      "_" +
      touch.medium
    );
  }



  /*
   * =========================================================
   * GOCERTIFY ATTRIBUTION
   * =========================================================
   *
   * GoCertify reads clickref when the custom element is
   * first connected to the page.
   *
   * Therefore we do NOT add clickref to an already-mounted
   * <gocertify-iframe>.
   *
   * Instead:
   *
   * 1. Read Hazaar's stored attribution.
   * 2. Create a fresh <gocertify-iframe> while detached.
   * 3. Copy the GoCertify configuration attributes.
   * 4. Add clickref before the element enters the DOM.
   * 5. Replace the original element with the fresh one.
   *
   * Example:
   *
   * clickref="google_cpc"
   *
   * GoCertify then passes this through as:
   *
   * student--campaign_id--verification_id--google_cpc
   */

  function getGoCertifyClickRef() {

    if (!marketingAllowed()) {
      return "";
    }


    const firstTouch =
      readRecord(
        FIRST_TOUCH_KEY
      );


    const lastTouch =
      readRecord(
        LAST_TOUCH_KEY
      );


    /*
     * Prefer last non-direct.
     * Fall back to first touch.
     */
    const convertingTouch =
      lastTouch.source
        ? lastTouch
        : firstTouch;


    return channelName(
      convertingTouch
    );
  }



  function rebuildGoCertifyWidget(
    widget
  ) {

    if (
      !widget ||
      !widget.isConnected
    ) {
      return widget;
    }


    /*
     * Hazaar already created this instance
     * with clickref present before insertion.
     *
     * Do not rebuild it again.
     */
    if (
      widget.dataset
        .hazaarGoCertifyBuilt ===
      "true"
    ) {
      return widget;
    }


    /*
     * Never pass marketing attribution
     * without Marketing consent.
     */
    if (!marketingAllowed()) {
      return widget;
    }


    const clickref =
      getGoCertifyClickRef();


    /*
     * No known marketing attribution?
     *
     * Leave the original GoCertify widget
     * untouched. GoCertify can use its normal
     * "none" fallback.
     */
    if (!clickref) {
      return widget;
    }


    const newWidget =
      document.createElement(
        "gocertify-iframe"
      );


    /*
     * Retain the configuration supplied by
     * the GoCertify / Shopify app block.
     *
     * Example:
     * source="student-weekday"
     */
    copyGoCertifyBaseAttributes(
      widget,
      newWidget
    );


    /*
     * Remember whether GoCertify originally
     * supplied its own clickref so it can be
     * restored if Marketing consent is later
     * withdrawn.
     */
    newWidget.dataset
      .hazaarHadOriginalClickref =
      widget.hasAttribute(
        "clickref"
      )
        ? "true"
        : "false";


    if (
      widget.hasAttribute(
        "clickref"
      )
    ) {

      newWidget.dataset
        .hazaarOriginalClickref =
        widget.getAttribute(
          "clickref"
        ) ||
        "";
    }


    /*
     * Mark the fresh component BEFORE inserting
     * it so the MutationObserver does not create
     * an endless replacement loop.
     */
    newWidget.dataset
      .hazaarGoCertifyBuilt =
      "true";


    /*
     * Critical:
     *
     * clickref must exist BEFORE the custom
     * element is inserted into the document.
     */
    newWidget.setAttribute(
      "clickref",
      clickref
    );


    /*
     * GoCertify now initialises this fresh
     * component with clickref already present.
     */
    widget.replaceWith(
      newWidget
    );


    return newWidget;
  }



  function rebuildGoCertifyWidgets() {

    const widgets =
      Array.from(
        document.querySelectorAll(
          "gocertify-iframe"
        )
      );


    widgets.forEach(
      rebuildGoCertifyWidget
    );
  }



  /*
   * Shopify / GoCertify may add widgets after
   * this attribution script has started.
   *
   * When a new original widget appears, replace
   * it with a fresh component that has clickref
   * set before connection.
   */

  const goCertifyObserver =
    new MutationObserver(
      function (mutations) {

        mutations.forEach(
          function (mutation) {

            mutation.addedNodes
              .forEach(
                function (node) {

                  if (
                    !node ||
                    node.nodeType !==
                      1
                  ) {
                    return;
                  }


                  /*
                   * The added node itself
                   * is a GoCertify widget.
                   */
                  if (
                    node.matches &&
                    node.matches(
                      "gocertify-iframe"
                    )
                  ) {

                    rebuildGoCertifyWidget(
                      node
                    );
                  }


                  /*
                   * Or one or more GoCertify
                   * widgets sit inside it.
                   */
                  if (
                    node.querySelectorAll
                  ) {

                    node
                      .querySelectorAll(
                        "gocertify-iframe"
                      )
                      .forEach(
                        rebuildGoCertifyWidget
                      );
                  }

                }
              );

          }
        );

      }
    );


  goCertifyObserver.observe(
    document.documentElement,
    {
      childList:
        true,

      subtree:
        true
    }
  );



  /*
   * =========================================================
   * AWIN ATTRIBUTION
   * =========================================================
   */

  function decorateAwinLink(
    link
  ) {

    /*
     * Never add marketing attribution
     * without consent.
     */
    if (!marketingAllowed()) {
      return;
    }


    try {

      const firstTouch =
        readRecord(
          FIRST_TOUCH_KEY
        );


      const lastTouch =
        readRecord(
          LAST_TOUCH_KEY
        );



      /*
       * Prefer latest non-direct touch.
       *
       * Fall back to first touch.
       */
      const convertingTouch =
        lastTouch.source
          ? lastTouch
          : firstTouch;



      /*
       * No attribution?
       *
       * Leave the original AWIN URL alone.
       */
      if (
        !convertingTouch.source
      ) {

        return;
      }



      /*
       * Store original affiliate URL.
       */
      if (
        !link.dataset
          .hazaarOriginalHref
      ) {

        link.dataset
          .hazaarOriginalHref =
          link.href;
      }



      const originalUrl =
        new URL(
          link.dataset
            .hazaarOriginalHref
        );



      /*
       * Existing links currently contain:
       *
       * clickref=brand_page
       *
       * Preserve that as the Hazaar
       * placement reference.
       */
      let placement =
        link.dataset
          .hazaarPlacement;


      if (!placement) {

        placement =
          originalUrl
            .searchParams
            .get(
              "clickref4"
            ) ||

          originalUrl
            .searchParams
            .get(
              "clickref"
            ) ||

          "unknown";


        link.dataset
          .hazaarPlacement =
          placement;
      }



      const url =
        new URL(
          link.dataset
            .hazaarOriginalHref
        );


      const convertingChannel =
        channelName(
          convertingTouch
        );


      const firstChannel =
        channelName(
          firstTouch
        );



      /*
       * clickref
       *
       * Latest non-direct marketing channel.
       */
      if (convertingChannel) {

        url.searchParams.set(
          "clickref",
          convertingChannel
        );
      }



      /*
       * clickref2
       *
       * Latest campaign.
       */
      if (
        convertingTouch
          .campaign
      ) {

        url.searchParams.set(
          "clickref2",
          convertingTouch
            .campaign
        );

      } else {

        url.searchParams.delete(
          "clickref2"
        );
      }



      /*
       * clickref3
       *
       * Original acquisition channel.
       */
      if (firstChannel) {

        url.searchParams.set(
          "clickref3",
          firstChannel
        );

      } else {

        url.searchParams.delete(
          "clickref3"
        );
      }



      /*
       * clickref4
       *
       * Hazaar CTA placement.
       *
       * Example:
       * brand_page
       */
      url.searchParams.set(
        "clickref4",
        clean(
          placement
        )
      );



      /*
       * clickref5
       *
       * Original Hazaar landing page.
       *
       * Homepage = home
       */
      if (
        firstTouch
          .landing_page
      ) {

        url.searchParams.set(
          "clickref5",
          cleanPath(
            firstTouch
              .landing_page
          )
        );

      } else {

        url.searchParams.delete(
          "clickref5"
        );
      }



      /*
       * clickref6
       *
       * Latest creative / utm_content.
       */
      if (
        convertingTouch
          .content
      ) {

        url.searchParams.set(
          "clickref6",
          convertingTouch
            .content
        );

      } else {

        url.searchParams.delete(
          "clickref6"
        );
      }



      link.href =
        url.toString();


    } catch (error) {

      console.error(
        "Hazaar attribution: unable to decorate AWIN link",
        error
      );
    }
  }



  /*
   * =========================================================
   * RAKUTEN U1 BUILDER
   * =========================================================
   *
   * Rakuten gives us one Signature value: u1.
   *
   * Format:
   *
   * L = last non-direct channel
   * C = latest campaign
   * F = first-touch channel
   * P = Hazaar placement
   *
   * Example:
   *
   * LgooglecpcCweekdaystudentdiscountFgooglecpcPbrandpage
   *
   * Values themselves are lowercase.
   * Uppercase L/C/F/P act as separators.
   */

  function buildRakutenU1(
    convertingTouch,
    firstTouch,
    placement
  ) {

    const lastChannel =
      cleanRakuten(
        channelName(
          convertingTouch
        ),
        22
      ) ||
      "none";


    const firstChannel =
      cleanRakuten(
        channelName(
          firstTouch
        ),
        22
      ) ||
      "none";


    const cleanPlacement =
      cleanRakuten(
        placement ||
          "unknown",
        12
      ) ||
      "unknown";


    /*
     * Build fixed parts first.
     *
     * Whatever space remains is used
     * for the campaign.
     */
    const prefix =
      "L" +
      lastChannel +
      "C";


    const suffix =
      "F" +
      firstChannel +
      "P" +
      cleanPlacement;


    const remainingLength =
      Math.max(
        0,

        RAKUTEN_U1_MAX_LENGTH -
          prefix.length -
          suffix.length
      );


    const campaign =
      cleanRakuten(
        convertingTouch
          .campaign ||
          "none",

        remainingLength
      );


    return (
      prefix +
      campaign +
      suffix
    ).substring(
      0,
      RAKUTEN_U1_MAX_LENGTH
    );
  }



  /*
   * =========================================================
   * RAKUTEN ATTRIBUTION
   * =========================================================
   */

  function decorateRakutenLink(
    link
  ) {

    /*
     * Never add marketing attribution
     * without consent.
     */
    if (!marketingAllowed()) {
      return;
    }


    try {

      const firstTouch =
        readRecord(
          FIRST_TOUCH_KEY
        );


      const lastTouch =
        readRecord(
          LAST_TOUCH_KEY
        );


      const convertingTouch =
        lastTouch.source
          ? lastTouch
          : firstTouch;



      /*
       * No marketing attribution?
       *
       * Leave the original Rakuten URL alone.
       */
      if (
        !convertingTouch.source
      ) {

        return;
      }



      /*
       * Store original Rakuten URL.
       */
      if (
        !link.dataset
          .hazaarOriginalHref
      ) {

        link.dataset
          .hazaarOriginalHref =
          link.href;
      }



      const originalUrl =
        new URL(
          link.dataset
            .hazaarOriginalHref
        );



      /*
       * Existing Rakuten links currently use:
       *
       * u1=brand_page
       *
       * Preserve this as the placement.
       */
      let placement =
        link.dataset
          .hazaarPlacement;


      if (!placement) {

        placement =
          originalUrl
            .searchParams
            .get(
              "u1"
            ) ||
          "unknown";


        link.dataset
          .hazaarPlacement =
          placement;
      }



      const url =
        new URL(
          link.dataset
            .hazaarOriginalHref
        );


      const u1 =
        buildRakutenU1(
          convertingTouch,
          firstTouch,
          placement
        );



      /*
       * Replace existing u1=brand_page
       * with our richer Signature value.
       */
      url.searchParams.set(
        "u1",
        u1
      );


      link.href =
        url.toString();


    } catch (error) {

      console.error(
        "Hazaar attribution: unable to decorate Rakuten link",
        error
      );
    }
  }



  /*
   * =========================================================
   * AFFILIATE LINK HANDLING
   * =========================================================
   */

  function handleAffiliateInteraction(
    event
  ) {

    /*
     * Guard against unexpected event targets.
     */
    if (
      !event.target ||
      typeof event.target
        .closest !==
        "function"
    ) {

      return;
    }



    /*
     * ---------------------------------------------------------
     * AWIN
     * ---------------------------------------------------------
     */

    const awinLink =
      event.target.closest(
        'a[href*="awin1.com/cread.php"]'
      );


    if (awinLink) {

      decorateAwinLink(
        awinLink
      );

      return;
    }



    /*
     * ---------------------------------------------------------
     * RAKUTEN
     * ---------------------------------------------------------
     */

    const rakutenLink =
      event.target.closest(
        'a[href*="click.linksynergy.com/deeplink"]'
      );


    if (rakutenLink) {

      decorateRakutenLink(
        rakutenLink
      );
    }
  }



  /*
   * pointerdown decorates links before:
   *
   * - normal click
   * - middle click
   * - right-click / open in new tab
   */

  document.addEventListener(
    "pointerdown",
    handleAffiliateInteraction,
    true
  );



  /*
   * Click fallback, including keyboard activation.
   */

  document.addEventListener(
    "click",
    handleAffiliateInteraction,
    true
  );



  /*
   * =========================================================
   * CONSENT MANAGEMENT
   * =========================================================
   */

  function syncConsentState() {

    if (
      marketingAllowed()
    ) {

      /*
       * Capture/update attribution first.
       */
      captureAttribution();


      /*
       * Then ensure each GoCertify component
       * is initialised with clickref already
       * present before it enters the DOM.
       */
      rebuildGoCertifyWidgets();


    } else {

      /*
       * Marketing consent withdrawn:
       *
       * - clear attribution storage
       * - restore original AWIN links
       * - restore original Rakuten links
       * - rebuild Hazaar-managed GoCertify
       *   widgets without Hazaar attribution
       */
      clearAttribution();
    }
  }



  /*
   * Shopify fires this when
   * visitor consent changes.
   */

  document.addEventListener(
    "visitorConsentCollected",
    syncConsentState
  );



  /*
   * =========================================================
   * WAIT FOR SHOPIFY CUSTOMER PRIVACY API
   * =========================================================
   */

  let attempts =
    0;


  const interval =
    setInterval(
      function () {

        attempts +=
          1;


        const ready =
          window.Shopify &&
          window.Shopify
            .customerPrivacy &&
          typeof window.Shopify
            .customerPrivacy
            .marketingAllowed ===
            "function";


        if (ready) {

          clearInterval(
            interval
          );


          syncConsentState();
        }


        /*
         * Stop after roughly 10 seconds.
         *
         * If Shopify Privacy never loads,
         * attribution fails closed.
         */
        if (
          attempts >=
            100
        ) {

          clearInterval(
            interval
          );
        }

      },
      100
    );



  /*
   * =========================================================
   * DEBUG: ATTRIBUTION
   * =========================================================
   *
   * Console:
   *
   * hazaarAttribution()
   */

  window.hazaarAttribution =
    function () {

      const output = {

        marketing_allowed:
          marketingAllowed(),

        first_touch:
          readRecord(
            FIRST_TOUCH_KEY
          ),

        last_non_direct:
          readRecord(
            LAST_TOUCH_KEY
          )
      };


      console.log(
        "Hazaar attribution"
      );


      console.table(
        output
      );


      return output;
    };



  /*
   * =========================================================
   * DEBUG: GOCERTIFY
   * =========================================================
   *
   * Console:
   *
   * hazaarGoCertifyTest()
   *
   * Ensures any current original GoCertify
   * widget is rebuilt correctly, then reports
   * the active component.
   */

  window.hazaarGoCertifyTest =
    function () {

      rebuildGoCertifyWidgets();


      const firstTouch =
        readRecord(
          FIRST_TOUCH_KEY
        );


      const lastTouch =
        readRecord(
          LAST_TOUCH_KEY
        );


      const widgets =
        Array.from(
          document.querySelectorAll(
            "gocertify-iframe"
          )
        );


      const output =
        widgets.map(
          function (widget) {

            return {

              id:
                widget.id ||
                "",

              source:
                widget.getAttribute(
                  "source"
                ) ||
                "",

              clickref:
                widget.getAttribute(
                  "clickref"
                ) ||
                "",

              created_with_clickref:
                widget.dataset
                  .hazaarGoCertifyBuilt ===
                  "true",

              first_touch:
                channelName(
                  firstTouch
                ),

              last_non_direct:
                channelName(
                  lastTouch
                )
            };
          }
        );


      console.log(
        "Hazaar GoCertify widgets"
      );


      console.table(
        output
      );


      return output;
    };



  /*
   * =========================================================
   * DEBUG: AWIN
   * =========================================================
   *
   * Console:
   *
   * hazaarAwinTest()
   *
   * Decorates AWIN links on the current
   * page without clicking away.
   */

  window.hazaarAwinTest =
    function () {

      const links =
        Array.from(
          document.querySelectorAll(
            'a[href*="awin1.com/cread.php"]'
          )
        );


      links.forEach(
        decorateAwinLink
      );


      const output =
        links.map(
          function (link) {

            const url =
              new URL(
                link.href
              );


            return {

              text:
                (
                  link.textContent ||
                  ""
                ).trim(),

              clickref:
                url.searchParams.get(
                  "clickref"
                ),

              clickref2:
                url.searchParams.get(
                  "clickref2"
                ),

              clickref3:
                url.searchParams.get(
                  "clickref3"
                ),

              clickref4:
                url.searchParams.get(
                  "clickref4"
                ),

              clickref5:
                url.searchParams.get(
                  "clickref5"
                ),

              clickref6:
                url.searchParams.get(
                  "clickref6"
                ),

              href:
                link.href
            };
          }
        );


      console.log(
        "Hazaar AWIN links"
      );


      console.table(
        output
      );


      return output;
    };



  /*
   * =========================================================
   * DEBUG: RAKUTEN
   * =========================================================
   *
   * Console:
   *
   * hazaarRakutenTest()
   *
   * Decorates Rakuten links on the current
   * page without clicking away.
   */

  window.hazaarRakutenTest =
    function () {

      const links =
        Array.from(
          document.querySelectorAll(
            'a[href*="click.linksynergy.com/deeplink"]'
          )
        );


      links.forEach(
        decorateRakutenLink
      );


      const output =
        links.map(
          function (link) {

            const url =
              new URL(
                link.href
              );


            const u1 =
              url.searchParams.get(
                "u1"
              );


            return {

              text:
                (
                  link.textContent ||
                  ""
                ).trim(),

              u1:
                u1,

              u1_length:
                u1
                  ? u1.length
                  : 0,

              mid:
                url.searchParams.get(
                  "mid"
                ),

              destination:
                url.searchParams.get(
                  "murl"
                ),

              href:
                link.href
            };
          }
        );


      console.log(
        "Hazaar Rakuten links"
      );


      console.table(
        output
      );


      return output;
    };


})();